import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useColorContext } from '../../../context/colorContext';

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback) => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default function PrivacyPolicy() {
  const { bgPrimary, bgSecondary, textPrimary, textSecondary, activeAccent, varkColors } = useColorContext();

  const safeBgPrimary = toSafeHex(bgPrimary, '#0A0A0F');
  const safeBgSecondary = toSafeHex(bgSecondary, '#13131A');
  const safeTextPrimary = toSafeHex(textPrimary, '#FFFFFF');
  const safeTextSecondary = toSafeHex(textSecondary, '#A1A1AA');
  const safeAccent = toSafeHex(activeAccent, '#6366F1');
  const visualColor = toSafeHex(varkColors?.visual, '#7C3AED');
  const auditoryColor = toSafeHex(varkColors?.auditory, '#3B82F6');

  const containerStyle = {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(visualColor, 0.05, safeBgPrimary)} 20%, ${hexToRgba(auditoryColor, 0.05, safeBgPrimary)} 45%, ${hexToRgba(safeAccent, 0.06, safeBgPrimary)} 70%, ${safeBgPrimary} 100%)`,
    color: safeTextPrimary,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    padding: '160px 24px 64px',
  };

  const contentStyle = {
    maxWidth: 900,
    margin: '0 auto',
  };

  const titleStyle = {
    fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
    fontWeight: 800,
    marginBottom: 12,
    background: `linear-gradient(135deg, ${safeTextPrimary} 0%, ${hexToRgba(safeAccent, 0.9, safeAccent)} 100%)`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const lastUpdatedStyle = {
    fontSize: '0.95rem',
    color: safeTextSecondary,
    marginBottom: 48,
    fontStyle: 'italic',
  };

  const headingStyle = (level) => ({
    fontSize: level === 2 ? '1.8rem' : '1.4rem',
    fontWeight: level === 2 ? 700 : 600,
    marginTop: level === 2 ? 40 : 28,
    marginBottom: 16,
    color: safeTextPrimary,
  });

  const textStyle = {
    fontSize: '1rem',
    lineHeight: 1.7,
    color: safeTextSecondary,
    marginBottom: 16,
  };

  const ulStyle = {
    listStyle: 'disc',
    paddingLeft: 24,
    marginBottom: 16,
    color: safeTextSecondary,
  };

  const liStyle = {
    marginBottom: 8,
    lineHeight: 1.6,
  };

  const boldTextStyle = {
    color: safeTextPrimary,
    fontWeight: 600,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 24,
    border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    borderRadius: 12,
    overflow: 'hidden',
  };

  const thStyle = {
    background: hexToRgba(safeBgSecondary, 0.8, safeBgSecondary),
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    color: safeTextPrimary,
  };

  const tdStyle = {
    padding: '12px 16px',
    borderBottom: `1px solid ${hexToRgba(safeAccent, 0.1, safeAccent)}`,
    color: safeTextSecondary,
  };

  const contactStyle = {
    background: hexToRgba(safeBgSecondary, 0.6, safeBgSecondary),
    padding: '20px 24px',
    borderRadius: 12,
    border: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}`,
    marginTop: 20,
    color: safeTextSecondary,
  };

  return (
    <div style={containerStyle}>
      <Helmet>
        <title>Privacy Policy — Plynote</title>
        <meta name="description" content="Read Plynote's privacy policy. Learn how we collect, use, and protect your personal data when using our AI note agent." />
        <link rel="canonical" href="https://www.plynote.io/privacy-policy" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:opsz,wght@14..32,300;14..32,400;14..32,500;14..32,600;14..32,700;14..32,800&display=swap');

          body {
            font-family: 'Inter', sans-serif;
          }

          ::-webkit-scrollbar {
            width: 8px;
          }

          ::-webkit-scrollbar-track {
            background: ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)};
          }

          ::-webkit-scrollbar-thumb {
            background: ${hexToRgba(safeAccent, 0.5, safeAccent)};
            border-radius: 4px;
          }

          ::-webkit-scrollbar-thumb:hover {
            background: ${hexToRgba(safeAccent, 0.7, safeAccent)};
          }
        `}
      </style>

      <div style={contentStyle}>
        <h1 style={titleStyle}>Privacy Policy</h1>
        <div style={lastUpdatedStyle}>Last updated: April 28, 2026</div>

        <section>
          <h2 style={headingStyle(2)}>1. Introduction</h2>
          <p style={textStyle}>
            Plynote ("we", "our", or "us") respects your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our application ("Plynote").
          </p>
          <p style={textStyle}>
            By using Plynote, you consent to the data practices described in this policy.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>2. Information We Collect</h2>
          
          <h3 style={headingStyle(3)}>2.1 Information you provide</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><span style={boldTextStyle}>Account information:</span> Email address, name (optional), payment information (processed by Stripe, not stored by us)</li>
            <li style={liStyle}><span style={boldTextStyle}>User content:</span> Documents you upload (PDFs, audio files, YouTube links), notes, annotations, chat history</li>
            <li style={liStyle}><span style={boldTextStyle}>Communication:</span> Messages you send us (support, feedback)</li>
          </ul>

          <h3 style={headingStyle(3)}>2.2 Information automatically collected</h3>
          <ul style={ulStyle}>
            <li style={liStyle}><span style={boldTextStyle}>Usage data:</span> Pages visited, features used, time spent</li>
            <li style={liStyle}><span style={boldTextStyle}>Device data:</span> Browser type, IP address, operating system</li>
            <li style={liStyle}><span style={boldTextStyle}>Cookies:</span> Essential cookies (authentication) and analytics cookies (optional)</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>3. How We Use Your Information</h2>
          <p style={textStyle}>We use your information to:</p>
          <ul style={ulStyle}>
            <li style={liStyle}>Provide and maintain Plynote</li>
            <li style={liStyle}>Process your payments (via Stripe)</li>
            <li style={liStyle}>Generate mindmaps, flashcards, audio summaries, and smart text from your documents</li>
            <li style={liStyle}>Improve our AI models (anonymized, no personal data)</li>
            <li style={liStyle}>Respond to your support requests</li>
            <li style={liStyle}>Send transactional emails (welcome, billing, security)</li>
          </ul>
        </section>

        <section>
          <h2 style={headingStyle(2)}>4. Data Sharing</h2>
          <p style={textStyle}>We share your data only with trusted third parties:</p>
          
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Third Party</th>
                <th style={thStyle}>Purpose</th>
                <th style={thStyle}>Data shared</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Stripe</span></td>
                <td style={tdStyle}>Payment processing</td>
                <td style={tdStyle}>Payment info, email</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>OpenAI / Google AI</span></td>
                <td style={tdStyle}>Document processing</td>
                <td style={tdStyle}>Document content (temporarily)</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Supabase</span></td>
                <td style={tdStyle}>Database hosting</td>
                <td style={tdStyle}>All user data</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Vercel</span></td>
                <td style={tdStyle}>Application hosting</td>
                <td style={tdStyle}>Usage data</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Plausible</span> (optional)</td>
                <td style={tdStyle}>Analytics</td>
                <td style={tdStyle}>Anonymous usage data</td>
              </tr>
            </tbody>
          </table>

          <p style={textStyle}>
            We never sell your personal data to third parties.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>5. Data Retention</h2>
          <ul style={ulStyle}>
            <li style={liStyle}><span style={boldTextStyle}>Account data:</span> Retained until you delete your account</li>
            <li style={liStyle}><span style={boldTextStyle}>Documents and notes:</span> Retained while your subscription is active + 30 days after cancellation</li>
            <li style={liStyle}><span style={boldTextStyle}>Chat history:</span> Retained while your account is active</li>
            <li style={liStyle}><span style={boldTextStyle}>Payment data:</span> Stored by Stripe according to their policies</li>
          </ul>
          <p style={textStyle}>
            You can request deletion of your data at any time (see Section 8).
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>6. Your Rights (GDPR - Europe)</h2>
          <p style={textStyle}>
            If you are in the European Economic Area (EEA), you have:
          </p>

          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Right</th>
                <th style={thStyle}>What it means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Access</span></td>
                <td style={tdStyle}>Request a copy of your data</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Rectification</span></td>
                <td style={tdStyle}>Correct inaccurate data</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Erasure</span></td>
                <td style={tdStyle}>Request deletion of your data ("right to be forgotten")</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Restriction</span></td>
                <td style={tdStyle}>Limit how we use your data</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Portability</span></td>
                <td style={tdStyle}>Receive your data in a machine-readable format</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Objection</span></td>
                <td style={tdStyle}>Object to certain processing (e.g., marketing)</td>
              </tr>
              <tr>
                <td style={tdStyle}><span style={boldTextStyle}>Withdraw consent</span></td>
                <td style={tdStyle}>Stop any processing based on consent</td>
              </tr>
            </tbody>
          </table>

          <p style={textStyle}>
            To exercise these rights, contact: <span style={boldTextStyle}>plynote.sender@gmail.com</span>
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>7. Your Rights (CCPA - California)</h2>
          <p style={textStyle}>
            If you are a California resident, you have the right to:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>Know what personal data we collect about you</li>
            <li style={liStyle}>Request deletion of your personal data</li>
            <li style={liStyle}>Opt-out of the sale of your personal data (we do not sell data)</li>
            <li style={liStyle}>Non-discrimination for exercising your rights</li>
          </ul>
          <p style={textStyle}>
            To exercise these rights, contact: <span style={boldTextStyle}>plynote.sender@gmail.com</span>
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>8. Data Security</h2>
          <p style={textStyle}>
            We implement industry-standard security measures:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>Encryption at rest (Supabase)</li>
            <li style={liStyle}>Encryption in transit (TLS/SSL)</li>
            <li style={liStyle}>Regular security audits</li>
            <li style={liStyle}>Limited employee access to user data</li>
          </ul>
          <p style={textStyle}>
            However, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>9. Children's Privacy</h2>
          <p style={textStyle}>
            Plynote is not intended for children under 13. We do not knowingly collect data from children under 13.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>10. International Data Transfers</h2>
          <p style={textStyle}>
            Your data may be transferred to and processed in:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}>United States (Vercel, OpenAI, Google)</li>
            <li style={liStyle}>Europe (Supabase)</li>
          </ul>
          <p style={textStyle}>
            We ensure adequate safeguards (Standard Contractual Clauses) for these transfers.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>11. Cookies</h2>
          <p style={textStyle}>
            We use:
          </p>
          <ul style={ulStyle}>
            <li style={liStyle}><span style={boldTextStyle}>Essential cookies:</span> Required for authentication and payments (no consent needed)</li>
          </ul>

        </section>

        <section>
          <h2 style={headingStyle(2)}>12. Changes to This Policy</h2>
          <p style={textStyle}>
            We may update this Privacy Policy. We will notify you of material changes by email (if you have an account) or via a notice on our website.
          </p>
        </section>

        <section>
          <h2 style={headingStyle(2)}>13. Contact Us</h2>
          <p style={textStyle}>
            For privacy questions or requests:
          </p>
          <div style={contactStyle}>
            <p style={{ marginBottom: 8 }}>📧 <span style={boldTextStyle}>plynote.sender@gmail.com</span></p>
            <p style={{ marginBottom: 8 }}>@Plynote</p>
          </div>
        </section>

        <section style={{ marginTop: 60, paddingTop: 40, borderTop: `1px solid ${hexToRgba(safeAccent, 0.2, safeAccent)}` }}>
          <Link to="/terms-of-service" style={{ textDecoration: 'none', color: 'inherit' }}>
            <p style={{ ...textStyle, color: safeAccent, fontSize: '1.1rem', fontWeight: 600, cursor: 'pointer' }}>
              → Read our Terms of Service
            </p>
          </Link>
        </section>
      </div>
    </div>
  );
}
